<template>
  <div class="container-fluid rb-page mt-4 mb-5">
    <header class="rb-head">
      <div>
        <h3 class="mb-1">Regnskapsbilag</h3>
        <p class="rb-lead">
          Bilagene regnskapsføreren trenger: betalingsoppgjøret fra VIVA per dag, med gebyrene, og rapportene for omsetningen.
        </p>
      </div>
      <span v-if="isSuperAdmin" class="rb-merchant" title="Følger merchanten du er logget inn på">
        <font-awesome-icon :icon="['fas', 'store']" /> {{ merchant }}
      </span>
    </header>

    <section class="rb-card" aria-labelledby="rb-viva">
      <h4 id="rb-viva" class="rb-card__title">Betalingsoppgjør fra VIVA</h4>
      <p class="rb-card__text">
        VIVA setter inn kortbetalingene på VIVA-kontoen den dagen de klareres, med gebyrene trukket fra, og betaler ut til
        bankkontoen etter utbetalingsinnstillingene hos VIVA. En utbetaling er summen av <strong>Netto</strong> for dagene
        siden forrige utbetaling. Last ned dagene som bilag til utbetalingen.
      </p>

      <div v-if="!usesViva && !isSuperAdmin" class="rb-alert rb-alert--info">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
        Dere tar ikke betalt med VIVA, så det er ikke noe betalingsoppgjør fra VIVA her. Oppgjøret for kortbetalingene får
        dere fra betalingsleverandøren deres.
      </div>

      <template v-else>
        <form class="rb-search" @submit.prevent="load()">
          <div class="rb-quick" role="group" aria-label="Hurtigvalg">
            <button type="button" class="rb-chip" @click="month(0)">Denne måneden</button>
            <button type="button" class="rb-chip" @click="month(-1)">Forrige måned</button>
            <button type="button" class="rb-chip" @click="lastDays(7)">Siste 7 dager</button>
          </div>
          <div class="rb-fields" :class="{ 'rb-fields--account': accounts.length > 1 }">
            <div class="rb-field">
              <label class="rb-label" for="rb-from">Fra og med</label>
              <Datepicker id="rb-from" v-model="fromDay" :locale="nbLocale" inputFormat="dd.MM.yyyy"
                          :upperLimit="today" :lowerLimit="earliestDay" class="form-control" />
            </div>
            <div class="rb-field">
              <label class="rb-label" for="rb-to">Til og med</label>
              <Datepicker id="rb-to" v-model="toDay" :locale="nbLocale" inputFormat="dd.MM.yyyy"
                          :lowerLimit="earliestDay" class="form-control" />
            </div>
            <div v-if="accounts.length > 1" class="rb-field">
              <label class="rb-label" for="rb-account">VIVA-konto</label>
              <select id="rb-account" v-model="locationId" class="form-select" @change="load()">
                <option v-for="account in accounts" :key="account.locationId" :value="account.locationId">
                  {{ accountName(account) }}
                </option>
              </select>
            </div>
            <div class="rb-actions">
              <button type="submit" class="bo_btn" :disabled="loading">{{ loading ? 'Henter…' : 'Vis' }}</button>
            </div>
          </div>
          <p class="rb-hint">
            Oppgjørsdato er dagen VIVA satte pengene inn på VIVA-kontoen, ikke dagen kunden betalte. CAGISTA lagrer
            hver dag morgenen etter og tar vare på den i fem år etter regnskapsåret, så lenge dere er kunde. Én rapport
            dekker høyst {{ maxDays }} dager.
          </p>
        </form>

        <div v-if="error" class="rb-alert rb-alert--bad" role="alert">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" /> {{ error }}
          <span v-if="isSuperAdmin && result && result.configured === false">
            Fyll inn Data Services under <a href="/vivaPartnerConfig?tab=connection">VIVA Partner Config</a>.
          </span>
        </div>

        <div v-else-if="result && result.connected === false" class="rb-alert rb-alert--info">
          <font-awesome-icon :icon="['fas', 'info-circle']" />
          <template v-if="isSuperAdmin">{{ merchant }} har ingen VIVA-konto koblet til CAGISTA.</template>
          <template v-else>Dere har ingen VIVA-konto koblet til ennå, så det er ikke noe betalingsoppgjør å vise.</template>
        </div>

        <section v-else-if="result" class="rb-result" aria-live="polite">
          <div class="rb-summary">
            <div class="rb-summary__text">
              <div><strong>{{ periodText }}</strong><template v-if="accountText"> · VIVA-konto {{ accountText }}</template></div>
              <div v-if="days.length">
                Netto til utbetaling: <strong class="rb-strong-num">{{ kr(totals.net) }}</strong> · {{ countText }}
              </div>
              <!-- Where the days come from is for us, not for the merchant or the accountant (founder, 2026-10-25). -->
              <div v-if="days.length && isSuperAdmin" class="rb-archive-line">
                <font-awesome-icon :icon="['fas', liveDays ? 'cloud-download-alt' : 'archive']" aria-hidden="true" />
                {{ archiveText }}
              </div>
            </div>
            <div v-if="days.length" class="rb-downloads">
              <button type="button" class="bo_btn" :disabled="pdfBusy" @click="downloadPdf()">
                <font-awesome-icon :icon="['fas', 'file-pdf']" /> {{ pdfBusy ? 'Lager PDF…' : 'Last ned PDF' }}
              </button>
              <button type="button" class="bo_btn_secondary" @click="downloadXlsx()"
                      title="Dagene og transaksjonene i én Excel-fil, med beløp som tall og datoer som datoer">
                <font-awesome-icon :icon="['fas', 'file-excel']" /> Excel
              </button>
              <button type="button" class="bo_btn_secondary" @click="downloadCsv('days')">
                <font-awesome-icon :icon="['fas', 'file-csv']" /> CSV: dager
              </button>
              <button type="button" class="bo_btn_secondary" @click="downloadCsv('transactions')">
                <font-awesome-icon :icon="['fas', 'file-csv']" /> CSV: transaksjoner
              </button>
            </div>
          </div>

          <div v-for="(warning, index) in warnings" :key="index" class="rb-alert rb-alert--warn" role="alert">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" /> {{ warning }}
          </div>

          <div v-if="days.length === 0" class="rb-empty">
            <p class="mb-2"><strong>VIVA har ingen klarerte kortbetalinger {{ emptyText }}.</strong></p>
            <ul class="mb-0">
              <li>Betalinger fra de siste dagene er kanskje ikke klarert ennå. De kommer med på dagen VIVA klarerer dem.</li>
              <li>Gjør perioden større, eller velg «Forrige måned».</li>
            </ul>
          </div>

          <table v-else class="rb-table">
            <thead>
              <tr>
                <th scope="col">Oppgjørsdato</th>
                <th scope="col" class="rb-num">Betalinger</th>
                <th scope="col" class="rb-num">Salg</th>
                <th scope="col" class="rb-num">Refusjoner</th>
                <th scope="col" class="rb-num">Gebyr</th>
                <th scope="col" class="rb-num">Netto</th>
                <th scope="col"><span class="visually-hidden">Bilag</span></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="d in days" :key="d.date">
                <tr class="rb-row" :class="{ 'rb-row--open': open === d.date }" tabindex="0"
                    :aria-expanded="open === d.date ? 'true' : 'false'"
                    @click="toggle(d.date)" @keydown.enter.prevent="toggle(d.date)" @keydown.space.prevent="toggle(d.date)">
                  <td data-label="Oppgjørsdato" class="rb-day">
                    <span>
                      {{ dayShort(d.date) }}<span v-if="d.date === result.today" class="rb-muted"> · så langt i dag</span>
                      <font-awesome-icon v-if="d.archived" class="rb-stored" :icon="['fas', d.final ? 'lock' : 'archive']"
                                         :title="storedTitle(d)" :aria-label="storedTitle(d)" />
                      <span v-if="vivaMoves[d.date]" class="rb-list-note">
                        <font-awesome-icon :icon="['fas', 'info-circle']" aria-hidden="true" /> {{ vivaListShort(vivaMoves[d.date]) }}
                      </span>
                    </span>
                  </td>
                  <td data-label="Betalinger" class="rb-num">
                    <span>{{ d.payments }}<span v-if="d.refunds" class="rb-muted"> + {{ d.refunds }} ref.</span></span>
                  </td>
                  <td data-label="Salg" class="rb-num rb-amount">{{ kr(d.sales) }}</td>
                  <td data-label="Refusjoner" class="rb-num rb-amount rb-muted">{{ d.refunded ? kr(d.refunded) : '–' }}</td>
                  <td data-label="Gebyr" class="rb-num rb-amount rb-muted">
                    <span>{{ kr(d.fees) }}<span v-if="d.feesMissing" class="rb-flag" title="VIVA mangler gebyr for noen betalinger"> !</span></span>
                  </td>
                  <td data-label="Netto" class="rb-num rb-amount rb-net">{{ kr(d.net) }}</td>
                  <td class="rb-row-actions">
                    <button type="button" class="rb-link" :disabled="pdfBusy" @click.stop="downloadPdf(d.date)"
                            :aria-label="'Last ned PDF for ' + dayShort(d.date)">
                      <font-awesome-icon :icon="['fas', 'file-pdf']" /> PDF
                    </button>
                    <font-awesome-icon class="rb-chevron" :icon="['fas', open === d.date ? 'chevron-up' : 'chevron-down']" aria-hidden="true" />
                  </td>
                </tr>
                <tr v-if="open === d.date" class="rb-detail">
                  <td colspan="7">
                    <div v-if="vivaMoves[d.date]" class="rb-list-detail">
                      <font-awesome-icon :icon="['fas', 'info-circle']" aria-hidden="true" />
                      <div>
                        <p v-for="(paragraph, index) in vivaListLong(vivaMoves[d.date])" :key="index">{{ paragraph }}</p>
                      </div>
                    </div>
                    <table class="rb-lines">
                      <thead>
                        <tr>
                          <th scope="col">Betalt</th>
                          <th scope="col">Ordre</th>
                          <th scope="col">Hvor</th>
                          <th scope="col">Type</th>
                          <th scope="col" class="rb-num">Beløp</th>
                          <th scope="col" class="rb-num">Gebyr</th>
                          <th scope="col" class="rb-num">Netto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="t in d.transactions" :key="t.id || t.time">
                          <td data-label="Betalt" class="rb-day">
                            <span>{{ dateTime(t.time) }}<span v-if="listedLaterAtViva(t, d.date)" class="rb-muted"> · gjort opp {{ clock(t.clearedAt) }}</span></span>
                          </td>
                          <td data-label="Ordre" class="rb-ref">{{ t.reference || '–' }}</td>
                          <td data-label="Hvor">{{ channel(t.channel) }}</td>
                          <td data-label="Type">
                            <span>{{ t.kindLabel }}<span v-if="t.status && t.status !== 'F'" class="rb-muted"> · {{ t.statusLabel }}</span></span>
                          </td>
                          <td data-label="Beløp" class="rb-num rb-amount">{{ kr(t.amount) }}</td>
                          <td data-label="Gebyr" class="rb-num rb-amount rb-muted">
                            <span>{{ kr(t.fee) }}<span v-if="t.feesMissing" class="rb-flag" title="VIVA mangler gebyr"> !</span></span>
                          </td>
                          <td data-label="Netto" class="rb-num rb-amount">{{ kr(t.net) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="rb-total">
                <th scope="row" data-label="">Sum</th>
                <td data-label="Betalinger" class="rb-num">
                  <span>{{ totals.payments }}<span v-if="totals.refunds" class="rb-muted"> + {{ totals.refunds }} ref.</span></span>
                </td>
                <td data-label="Salg" class="rb-num rb-amount">{{ kr(totals.sales) }}</td>
                <td data-label="Refusjoner" class="rb-num rb-amount">{{ totals.refunded ? kr(totals.refunded) : '–' }}</td>
                <td data-label="Gebyr" class="rb-num rb-amount">{{ kr(totals.fees) }}</td>
                <td data-label="Netto" class="rb-num rb-amount rb-net">{{ kr(totals.net) }}</td>
                <td class="rb-row-actions"></td>
              </tr>
            </tfoot>
          </table>
        </section>

        <details class="rb-help">
          <summary>Slik bruker regnskapsføreren betalingsoppgjøret</summary>
          <ol class="rb-steps">
            <li>Finn utbetalingen fra VIVA på bankutskriften.</li>
            <li>
              Utbetalingen dekker oppgjørsdatoene siden forrige utbetaling. Hvilke dager VIVA har betalt ut, ser eieren i VIVA
              under <strong>Kontoer</strong> → <strong>Bankoverføringer</strong>
              <template v-if="!isAccountant">(se <router-link to="/vivaPayoutReports">Utbetalinger</router-link>)</template>.
            </li>
            <li>
              Summen av <strong>Netto</strong> for de dagene er det som ble utbetalt. Last ned PDF for dagene og legg den ved
              som bilag. Med daglige utbetalinger er det én dag per utbetaling: bruk PDF-knappen på dagen.
            </li>
            <li>
              <strong>Salg</strong> er kortsalget brutto, slik Z-rapporten har det. <strong>Gebyr</strong> er det som er
              trukket før utbetalingen.
            </li>
          </ol>
          <p class="rb-note">
            Det første beløpet VIVA betaler ut, kan være lavere enn summen av dagene: <strong>minimumsbeløpet</strong> i
            utbetalingsinnstillingene blir stående på VIVA-kontoen. Gebyret for selve overføringen til banken, hvis VIVA-planen
            tar det, og innsigelser fra kortholdere trekkes også fra VIVA-kontoen uten å stå her.
          </p>
          <p class="rb-note">
            <font-awesome-icon :icon="['fas', 'lock']" aria-hidden="true" /> <strong>Lagret og låst:</strong> CAGISTA lagrer
            hver dag morgenen etter, og etter en uke låses dagen og endres aldri mer. Bilagene tas vare på i fem år etter
            regnskapsåret, også når VIVA selv bare viser de siste {{ historyDays }} dagene. Revisoren kan få en egen bruker
            med rollen Regnskapsfører og hente dem her. Slettes kontoen hos CAGISTA, slettes bilagene med den, så last dem
            ned før dere slutter.
          </p>
        </details>

        <div v-if="isSuperAdmin" class="rb-admin">
          <button type="button" class="bo_btn_secondary" :disabled="archiving" @click="archiveNow()">
            <font-awesome-icon :icon="['fas', 'archive']" /> {{ archiving ? 'Lagrer…' : 'Lagre i arkivet nå' }}
          </button>
          <span class="rb-muted">Kun SUPER_ADMIN: gjør det morgenjobben gjør, for alle merchants. Låste dager røres ikke.</span>
          <div v-if="archiveResult" class="rb-alert" :class="archiveResult.ok ? 'rb-alert--info' : 'rb-alert--warn'">
            {{ archiveResultText }}
          </div>
        </div>
      </template>
    </section>

    <section class="rb-card" aria-labelledby="rb-other">
      <h4 id="rb-other" class="rb-card__title">Andre bilag</h4>
      <ul class="rb-links">
        <li>
          <router-link to="/z-report">Z-rapport (dagsoppgjør)</router-link>
          <span>Omsetningen hver dag, per kasse: salg per mva-sats og betalingsmåte. Bilaget for salget.</span>
        </li>
        <li>
          <router-link to="/totalSalesReport">Omsetningsrapport</router-link>
          <span>Omsetningen for en periode, fordelt på mva-satser og betalingsmåter.</span>
        </li>
        <li v-if="hasTills">
          <router-link to="/cashHoldingReport">Kontantbeholdning</router-link>
          <span>Kontantene i kassene, til avstemming av kontantsalget.</span>
        </li>
        <li v-if="hasTills">
          <router-link to="/saftReport">SAF-T Rapport</router-link>
          <span>Kassesystemets data i formatet Skatteetaten krever, for revisor og bokettersyn.</span>
        </li>
      </ul>
      <!-- Hidden for a merchant with no CAGISTA subscription, who has no receipts. -->
      <SubscriptionReceipts class="rb-receipts" hide-when-empty />
    </section>
  </div>
</template>

<script>
import VivaReportService from "@/services/system/VivaReportService";
import ReportPdfBuilder from "@/util/ReportPdfBuilder";
import VivaSettlementFormat from "@/util/VivaSettlementFormat";
import LocationUtil from "@/util/LocationUtil";
import MerchantUtil from "@/util/MerchantUtil";
import UserUtil from "@/util/UserUtil";
import Datepicker from "@/components/other/ToggleDatepicker.vue";
import SubscriptionReceipts from "@/components/billing/SubscriptionReceipts.vue";
import { nb } from "date-fns/locale";

/**
 * VIVA's own window, and the server's limits for one report (VivaSettlementService): the archive keeps every day for
 * five years after the end of its year, so a report reaches back to 1 January five years ago.
 */
const HISTORY_DAYS = 240;
const ARCHIVE_YEARS = 5;
const MAX_DAYS = 92;
const GENERIC_ERROR = "Kunne ikke hente betalingsoppgjøret fra VIVA akkurat nå. Prøv igjen om litt.";

/** A calendar day from a Date, built from local parts: toISOString() would move it to UTC first. */
function dayKey(date) {
  const d = new Date(date);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

/**
 * «Regnskapsbilag»: the bilag a merchant's accountant needs, in one place. VIVA's card settlement per clearing day,
 * with the fees as one «Gebyr» (VIVA's and CAGISTA's together, founder 2026-10-24), as a page, a PDF and two CSV files
 * for Excel; and the way to the reports that document the sales (Z-rapport, Omsetningsrapport, Kontantbeholdning,
 * SAF-T).
 *
 * For the merchant's ADMIN and ACCOUNTANT, and SUPER_ADMIN — not EMPLOYEE: fees are the owner's figures. Everything
 * comes from /api/report/getVivaSettlement, which keeps only this merchant's own VIVA account and has already added the
 * øre up; the page formats and adds nothing. The days come from CAGISTA's archive, stored each night and locked after a
 * week, and only the days not stored yet come live from VIVA; each day says which. Why a day is a clearing day, and
 * what the figures can and cannot prove, is in CagistaServices docs/accounting-vouchers.md.
 */
export default {
  name: "AccountingVouchers",
  components: { Datepicker, SubscriptionReceipts },
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      today,
      earliestDay: new Date(today.getFullYear() - ARCHIVE_YEARS, 0, 1),
      historyDays: HISTORY_DAYS,
      maxDays: MAX_DAYS,
      fromDay: new Date(today.getFullYear(), today.getMonth(), 1),
      toDay: new Date(today),
      locationId: "",
      loading: false,
      pdfBusy: false,
      error: "",
      result: null,
      open: "",
      archiving: false,
      archiveResult: null
    };
  },
  computed: {
    nbLocale() { return nb; },
    isSuperAdmin() { return UserUtil.isSuperAdmin(); },
    isAccountant() { return UserUtil.isAccountant(); },
    merchant() {
      const user = this.$store.state.auth.user;
      return user && user.merchant ? user.merchant : "";
    },
    /** The same test the menu uses for Kortbetalinger: a location on VIVA on the web or with a VIVA till setup. */
    usesViva() {
      return (LocationUtil.getLocations() || []).some(location =>
        location.onlinePaymentProvider === "VIVA" || (location.viva && location.viva.configured));
    },
    /** Kontantbeholdning and SAF-T are for tills; the Rapporter menu hides them for web-only merchants too. */
    hasTills() {
      const functionality = MerchantUtil.useFunctionality();
      return functionality !== "WEBSITE" && functionality !== "TAKEAWAY";
    },
    accounts() { return (this.result && this.result.accounts) || []; },
    /** Newest first on the page (founder, 2026-10-25); the PDF and the Excel files keep the days in date order. */
    days() {
      return ((this.result && this.result.days) || []).slice().sort((a, b) => b.date.localeCompare(a.date));
    },
    totals() { return (this.result && this.result.totals) || {}; },
    warnings() { return (this.result && this.result.warnings) || []; },
    /** Days VIVA's own Sales Clearance list shows otherwise, because VIVA counts the day in Greek time. */
    vivaMoves() { return VivaSettlementFormat.vivaListMoves(this.result); },
    periodText() {
      if (!this.result || !this.result.from) { return ""; }
      return "Oppgjørsdato " + VivaSettlementFormat.period(this.result.from, this.result.to);
    },
    accountText() {
      return this.accounts.length > 1 ? this.accountName(this.result.account) : "";
    },
    countText() {
      const n = this.totals.payments || 0;
      const r = this.totals.refunds || 0;
      return (n === 1 ? "1 betaling" : n + " betalinger") + (r ? ", " + (r === 1 ? "1 refusjon" : r + " refusjoner") : "");
    },
    emptyText() {
      if (!this.result || !this.result.from) { return "i perioden"; }
      return this.result.from === this.result.to
        ? "den " + VivaSettlementFormat.dayNumeric(this.result.from)
        : "fra " + VivaSettlementFormat.period(this.result.from, this.result.to).replace("–", " til ");
    },
    /** Days shown live from VIVA: every day not locked yet, unless only the stored copy could be shown. */
    liveDays() {
      return this.days.filter(d => !d.archived).length;
    },
    /** Locked at CAGISTA, live from VIVA, or the stored copy of a day not locked yet: the three add up to every day. */
    archiveText() {
      const total = this.days.length;
      const locked = this.days.filter(d => d.archived && d.final).length;
      const copied = this.days.filter(d => d.archived && !d.final).length;
      const count = n => (n === 1 ? "1 dag er" : n + " dager er");
      const parts = [];
      if (locked) {
        parts.push(locked === total ? "Alle dagene er låst hos CAGISTA."
          : locked + " av " + total + " dager er låst hos CAGISTA.");
      }
      if (this.liveDays) { parts.push(count(this.liveDays) + " hentet direkte fra VIVA og låses etter en uke."); }
      if (copied) { parts.push(count(copied) + " hentet fra kopien hos CAGISTA og er ikke låst ennå."); }
      return parts.join(" ");
    },
    archiveResultText() {
      const r = this.archiveResult;
      if (!r) { return ""; }
      if (r.error) { return r.error; }
      let text = "Lagret " + (r.written || 0) + " dager for " + (r.accounts || 0) + " VIVA-kontoer ("
        + VivaSettlementFormat.period(r.from, r.to) + "). " + (r.locked || 0) + " låste dager ble ikke rørt.";
      if (r.failedDays && r.failedDays.length) {
        text += " Ikke lagret: " + r.failedDays.map(d => VivaSettlementFormat.dayNumeric(d)).join(", ") + ".";
      }
      if (r.problems && r.problems.length) {
        text += " " + r.problems.join(" ");
      }
      return text;
    }
  },
  mounted() {
    if (this.usesViva || this.isSuperAdmin) {
      this.load();
    }
  },
  methods: {
    /** A calendar month, this one (0) or an earlier one; the server cuts a month that has not ended at today. */
    month(offset) {
      this.fromDay = new Date(this.today.getFullYear(), this.today.getMonth() + offset, 1);
      this.toDay = new Date(this.today.getFullYear(), this.today.getMonth() + offset + 1, 0);
      this.load();
    },
    lastDays(n) {
      this.fromDay = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() - (n - 1));
      this.toDay = new Date(this.today);
      this.load();
    },
    load() {
      if (!this.fromDay || !this.toDay) {
        this.error = "Velg første og siste dag.";
        return;
      }
      const params = { merchant: this.merchant, from: dayKey(this.fromDay), to: dayKey(this.toDay) };
      if (this.locationId) { params.locationId = this.locationId; }
      this.loading = true;
      this.error = "";
      this.open = "";
      VivaReportService.getSettlement(params).then(
        (response) => {
          const data = response.data || {};
          this.loading = false;
          this.result = data;
          if (data.account && data.account.locationId) {
            this.locationId = data.account.locationId;
          }
          if (data.ok === false) {
            this.error = data.error || GENERIC_ERROR;
          }
        },
        (error) => {
          this.loading = false;
          const body = error && error.response ? error.response.data : null;
          this.error = (body && body.error) || GENERIC_ERROR;
        }
      );
    },
    toggle(day) {
      this.open = this.open === day ? "" : day;
    },
    /** The location the PDF is headed with: the one standing for the VIVA account, with its name and org number. */
    headerLocation() {
      const id = this.result && this.result.account ? this.result.account.locationId : "";
      return (id && LocationUtil.getLocation(id)) || (LocationUtil.getLocations() || [])[0] || {};
    },
    /** The whole period, or with `day` the one clearing day. pdfmake needs a moment for a long month. */
    downloadPdf(day) {
      const r = this.result;
      const name = VivaSettlementFormat.fileName("VIVA-oppgjor", r.account, day || r.from, day || r.to, "pdf");
      this.pdfBusy = true;
      setTimeout(() => {
        try {
          ReportPdfBuilder.buildVivaSettlementPdf(r, this.headerLocation(), day, name);
        } finally {
          this.pdfBusy = false;
        }
      }, 0);
    },
    /** The days and the transactions as one Excel workbook, with kroner as numbers and days as dates. */
    downloadXlsx() {
      const r = this.result;
      VivaSettlementFormat.download(VivaSettlementFormat.workbook(r),
        VivaSettlementFormat.fileName("VIVA-oppgjor", r.account, r.from, r.to, "xlsx"),
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    },
    downloadCsv(kind) {
      const r = this.result;
      const content = kind === "days" ? VivaSettlementFormat.daysCsv(r) : VivaSettlementFormat.transactionsCsv(r);
      const prefix = kind === "days" ? "VIVA-oppgjor-dager" : "VIVA-transaksjoner";
      VivaSettlementFormat.download(content, VivaSettlementFormat.fileName(prefix, r.account, r.from, r.to, "csv"), "text/csv");
    },
    /** SUPER_ADMIN: the night's job now, then the page again so its days show as stored. */
    archiveNow() {
      this.archiving = true;
      this.archiveResult = null;
      VivaReportService.archiveSettlements().then(
        (response) => {
          this.archiving = false;
          this.archiveResult = response.data || {};
          this.load();
        },
        (error) => {
          this.archiving = false;
          const body = error && error.response ? error.response.data : null;
          this.archiveResult = { ok: false, error: (body && body.error) || "Arkiveringen feilet. Se application.log." };
        }
      );
    },
    /** «Lagret 15.10.2026 08:00», and whether it is locked. */
    storedTitle(day) {
      const when = day.capturedAt ? " " + VivaSettlementFormat.dateTime(day.capturedAt) : "";
      return (day.final ? "Lagret og låst hos CAGISTA" : "Lagret hos CAGISTA, låses etter en uke") + when;
    },
    kr(ore) { return VivaSettlementFormat.kr(ore); },
    dayShort(day) { return VivaSettlementFormat.dayShort(day); },
    dateTime(iso) { return VivaSettlementFormat.dateTime(iso); },
    clock(iso) { return VivaSettlementFormat.clock(iso); },
    vivaListShort(move) { return VivaSettlementFormat.vivaListShort(move); },
    vivaListLong(move) { return VivaSettlementFormat.vivaListLong(move); },
    listedLaterAtViva(transaction, day) { return VivaSettlementFormat.listedLaterAtViva(transaction, day); },
    channel(channel) { return VivaSettlementFormat.channel(channel); },
    accountName(account) { return VivaSettlementFormat.accountName(account); }
  }
};
</script>

<style scoped>
.rb-page { max-width: 1400px; margin: 0 auto; }

.rb-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.rb-lead { color: var(--bo-muted); margin: 0; }
.rb-merchant {
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px;
  background: var(--bo-brand-tint); color: var(--bo-brand-ink); font-weight: 600; font-size: 0.85rem;
  text-transform: uppercase; letter-spacing: 0.02em;
}

.rb-card {
  margin-top: 18px; padding: 18px 20px; border: 1px solid var(--bo-line); border-radius: 12px;
  background: var(--bo-surface); color: var(--bo-ink);
}
.rb-card__title { font-size: 1.15rem; font-weight: 600; margin: 0 0 8px; }
.rb-card__text { color: var(--bo-ink-2); max-width: 900px; margin-bottom: 14px; }

.rb-search { padding: 14px 16px; border: 1px solid var(--bo-line); border-radius: 10px; background: var(--bo-surface-2); }
.rb-quick { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.rb-chip {
  border: 1px solid var(--bo-line); background: var(--bo-surface); color: var(--bo-ink); border-radius: 999px;
  padding: 4px 14px; font-size: 0.9rem; min-height: 36px; transition: all .2s ease;
}
.rb-chip:hover { border-color: #ECB109; color: var(--bo-brand-ink); }
.rb-fields {
  display: grid; gap: 12px; align-items: end;
  grid-template-columns: minmax(150px, 220px) minmax(150px, 220px) auto;
}
.rb-fields--account { grid-template-columns: minmax(150px, 220px) minmax(150px, 220px) minmax(180px, 320px) auto; }
.rb-label { display: block; font-size: 0.85rem; color: var(--bo-muted); margin-bottom: 4px; }
.rb-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.rb-actions .bo_btn { min-height: 38px; }
.rb-hint { color: var(--bo-muted); font-size: 0.82rem; margin: 10px 0 0; }

.rb-alert { margin-top: 14px; padding: 10px 14px; border-radius: 8px; border: 1px solid; }
.rb-alert--bad { background: var(--bo-bad-bg); color: var(--bo-bad-fg); border-color: var(--bo-bad-line); }
.rb-alert--info { background: var(--bo-info-bg); color: var(--bo-info-fg); border-color: var(--bo-info-line); }
.rb-alert--warn { background: var(--bo-warn-bg); color: var(--bo-warn-fg); border-color: var(--bo-warn-line); }
.rb-alert a { color: inherit; text-decoration: underline; }

.rb-result { margin-top: 18px; }
.rb-summary { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.rb-summary__text { color: var(--bo-ink-2); line-height: 1.6; }
.rb-summary__text strong { color: var(--bo-ink); }
.rb-strong-num { font-variant-numeric: tabular-nums; }
.rb-downloads { display: flex; gap: 8px; flex-wrap: wrap; }
.rb-downloads .bo_btn, .rb-downloads .bo_btn_secondary { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 38px; }

.rb-empty {
  margin-top: 6px; padding: 16px; border: 1px dashed var(--bo-line); border-radius: 10px; color: var(--bo-ink-2);
  background: var(--bo-surface);
}

.rb-table { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--bo-surface);
  border: 1px solid var(--bo-line); border-radius: 10px; overflow: hidden; }
.rb-table > thead th { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bo-muted);
  font-weight: 600; padding: 10px 12px; border-bottom: 1px solid var(--bo-line); background: var(--bo-surface-2); }
.rb-table > tbody > tr > td, .rb-total > * { padding: 10px 12px; border-bottom: 1px solid var(--bo-line); vertical-align: top; color: var(--bo-ink); }
.rb-row { cursor: pointer; }
.rb-row:hover td, .rb-row--open td { background: var(--bo-surface-2); }
.rb-row:focus-visible { outline: 2px solid #ECB109; outline-offset: -2px; }
.rb-num { text-align: right; }
.rb-day { white-space: nowrap; font-weight: 600; }
.rb-amount { font-variant-numeric: tabular-nums; white-space: nowrap; }
.rb-net { font-weight: 700; }
.rb-muted { color: var(--bo-muted); font-weight: 400; }
.rb-flag { color: var(--bo-alert-ink); font-weight: 700; }
.rb-ref { overflow-wrap: anywhere; }
.rb-row-actions { white-space: nowrap; text-align: right; width: 1%; }
.rb-link {
  border: 0; background: none; color: var(--bo-brand-ink); font-weight: 600; padding: 2px 6px; border-radius: 6px;
}
.rb-link:hover { background: var(--bo-brand-tint); }
.rb-chevron { color: var(--bo-muted); margin-left: 6px; }
.rb-total > * { background: var(--bo-surface-2); font-weight: 700; border-bottom: 0; }

.rb-detail > td { background: var(--bo-surface-2); padding: 0 12px 12px; }
.rb-lines { width: 100%; border-collapse: collapse; background: var(--bo-surface); border: 1px solid var(--bo-line); border-radius: 8px; }
.rb-lines th { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bo-muted); font-weight: 600;
  padding: 8px 10px; border-bottom: 1px solid var(--bo-line); }
.rb-lines td { padding: 7px 10px; border-bottom: 1px solid var(--bo-line); font-size: 0.92rem; color: var(--bo-ink); }
.rb-lines tr:last-child td { border-bottom: 0; }

.rb-help { margin-top: 18px; color: var(--bo-ink-2); }
.rb-help summary { cursor: pointer; color: var(--bo-brand-ink); font-weight: 600; }
.rb-steps { margin: 12px 0 10px; padding-left: 1.4rem; max-width: 900px; }
.rb-steps li { margin-bottom: 6px; }
.rb-note { font-size: 0.92rem; max-width: 900px; margin: 0; }

.rb-list-note {
  display: block; max-width: 18rem; margin-top: 2px; white-space: normal; font-size: 0.8rem; font-weight: 400;
  color: var(--bo-info-fg);
}
.rb-list-detail {
  display: flex; gap: 8px; align-items: baseline; margin: 8px 0; padding: 8px 12px; border: 1px solid var(--bo-info-line);
  border-radius: 8px; background: var(--bo-info-bg); color: var(--bo-info-fg); font-size: 0.9rem;
}
.rb-list-detail p { margin: 0 0 4px; }
.rb-list-detail p:last-child { margin-bottom: 0; }

.rb-archive-line { color: var(--bo-muted); font-size: 0.9rem; }
.rb-stored { color: var(--bo-muted); margin-left: 6px; font-size: 0.8rem; }
.rb-admin { margin-top: 16px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.rb-admin .rb-alert { flex-basis: 100%; margin-top: 4px; }

.rb-links { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.rb-links li { display: grid; grid-template-columns: 220px 1fr; gap: 12px; align-items: baseline; }
.rb-links a { font-weight: 600; }
.rb-receipts { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--bo-line); }
.rb-links span { color: var(--bo-ink-2); }

/* A finger (touch.css): the quick choices and the downloads reach the 48px floor. */
@media (pointer: coarse) {
  .rb-chip { min-height: var(--bo-touch-target, 48px); padding: 8px 18px; }
  .rb-chip:hover { border-color: var(--bo-line); color: var(--bo-ink); }
  .rb-chip:active { background: var(--bo-brand-tint); border-color: #ECB109; }
  .rb-downloads .bo_btn, .rb-downloads .bo_btn_secondary, .rb-link { min-height: var(--bo-touch-target, 48px); }
}

@media (max-width: 991px) {
  .rb-fields, .rb-fields--account { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .rb-actions { grid-column: 1 / -1; }
}

/* A phone: each day and each payment is a card, the column name beside its value. */
@media (max-width: 767px) {
  .rb-table, .rb-table > tbody, .rb-table > tfoot, .rb-table > tbody > tr, .rb-table > tfoot > tr,
  .rb-table > tbody > tr > td, .rb-total > *,
  .rb-lines, .rb-lines tbody, .rb-lines tr, .rb-lines td { display: block; width: 100%; }
  .rb-table { border: 0; background: transparent; }
  .rb-table > thead, .rb-lines thead { display: none; }
  .rb-row, .rb-total { border: 1px solid var(--bo-line); border-radius: 10px; margin-bottom: 10px; background: var(--bo-surface); overflow: hidden; }
  .rb-row--open { border-bottom-left-radius: 0; border-bottom-right-radius: 0; margin-bottom: 0; }
  .rb-table > tbody > tr > td, .rb-total > *, .rb-lines td {
    border-bottom: 0; padding: 6px 12px; display: grid; grid-template-columns: 120px 1fr; gap: 8px;
    align-items: baseline; justify-items: start; text-align: left;
  }
  .rb-table > tbody > tr > td::before, .rb-total > *::before, .rb-lines td::before {
    content: attr(data-label); color: var(--bo-muted); font-size: 0.8rem; font-weight: 600; text-transform: none;
  }
  .rb-row-actions { display: flex !important; justify-content: flex-end; width: 100%; }
  .rb-row-actions::before { content: none !important; }
  .rb-detail { border: 1px solid var(--bo-line); border-top: 0; border-radius: 0 0 10px 10px; margin-bottom: 10px; overflow: hidden; }
  .rb-detail > td { display: block; padding: 8px; }
  .rb-detail > td::before { content: none !important; }
  .rb-lines { border: 0; background: transparent; }
  .rb-lines tr { border: 1px solid var(--bo-line); border-radius: 8px; margin-bottom: 8px; background: var(--bo-surface); padding: 4px 0; }
  .rb-links li { grid-template-columns: 1fr; gap: 2px; }
}
</style>
